// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { maskIban } from '@shared/advisory/aggregation/utils/bank-utils';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type { AccountTransaction } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTransactionLabels } from '../../hooks/use-transaction-labels';

export interface UseTransactionDrawerProps {
  transaction?: AccountTransaction | null;
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
}

const normalizeText = (value?: string | null) => value?.trim() ?? '';

export const CREATED_AT_FORMAT_OPTIONS = {
  day: 'numeric' as const,
  month: 'short' as const,
  year: 'numeric' as const,
  hour: '2-digit' as const,
  minute: '2-digit' as const,
};

export const useTransactionDrawer = (props: UseTransactionDrawerProps) => {
  const { open, onClose, onOpen, transaction: transactionProp } = props;

  const { t } = useTranslation();

  const { getTypeLabel, getSubtypeLabel } = useTransactionLabels();

  const selectedCurrency = useSelector(selectAccountCurrency);

  const [DrawerRenderer, drawerControls] = useDrawer({
    open,
    onClose,
    onOpen,
  });

  const transaction = transactionProp ?? null;

  documentsApi.useListDocumentsQuery(undefined, { skip: !open });

  const maskedIban = useMemo(
    () => maskIban(transaction?.iban, 'first-and-last'),
    [transaction?.iban],
  );

  const description = useMemo(
    () => normalizeText(transaction?.description),
    [transaction?.description],
  );

  const name = useMemo(() => normalizeText(transaction?.name), [transaction?.name]);

  const headerTitle = useMemo(
    () => description || name || normalizeText(transaction?.type) || '-',
    [description, name, transaction?.type],
  );

  const isAmountPositive = (transaction?.amount ?? 0) >= 0;

  return {
    DrawerRenderer,
    open: drawerControls.open,
    onClose: drawerControls.onClose,
    onOpen: drawerControls.onOpen,
    transaction,
    maskedIban,
    headerTitle,
    description,
    isAmountPositive,
    selectedCurrency,
    getTypeLabel,
    getSubtypeLabel,
    t,
  } as const;
};
